import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { API_BASE_URL } from '../constants/system'

// Where an agent output (a web app or a video) is already published to
// DataViz, so the publish dialog opens on the item it will update (#1578 item
// 13):
//
//   GET /agent/artifacts/:id/publish-targets
//     → { dataviz_type, publish_targets[], others[] }
//
// `others` are items a colleague published that this person may not update.
// Anyone who cannot open the chat gets a 404, which reads here as "not
// published yet".

export interface StoryPublishTarget {
  kind: 'share' | 'dashboard' | string
  id: string
  name?: string
  can_update?: boolean
  reason?: string
  // A dashboard's share settings.
  description?: string
  share_id?: string
  share_enabled?: boolean
  share_access_level?: string
  has_password?: boolean
}

/** An item published from the same output that this person may not update. */
export interface OtherPublishedItem {
  id: string
  name: string
  owner: string
}

interface PublishTargetsResponse {
  dataviz_type?: string | null
  publish_targets?: StoryPublishTarget[]
  others?: OtherPublishedItem[]
}

export const storyTargetsKey = (artifactId: string) => ['story-publish-targets', artifactId]

/** The targets Publish would update: the ones this person may write. */
export function updatableTargets(targets: StoryPublishTarget[]): StoryPublishTarget[] {
  return targets.filter(t => t.can_update !== false)
}

/**
 * The dashboard a publish of this web app updates: the newest one published
 * from it that this person may write. The server makes the same choice.
 */
export function publishedDashboard(targets: StoryPublishTarget[]): StoryPublishTarget | null {
  return updatableTargets(targets).find(t => t.kind === 'dashboard') ?? null
}

export default function useStoryBoardPublish(artifactId: string | null | undefined, enabled = true) {
  const targetsQuery = useQuery({
    queryKey: storyTargetsKey(artifactId || ''),
    enabled: !!artifactId && enabled,
    retry: false,
    // A 404 (not an output you can open) is an answer, not an error.
    queryFn: async (): Promise<PublishTargetsResponse> => {
      try {
        const res = await axios.get<PublishTargetsResponse>(`${API_BASE_URL}/agent/artifacts/${encodeURIComponent(artifactId!)}/publish-targets`)
        return res.data
      } catch (err: any) {
        if (err?.response?.status === 404) return { publish_targets: [] }
        throw err
      }
    },
  })

  const targets = targetsQuery.data?.publish_targets || []
  return {
    targets,
    published: publishedDashboard(targets),
    others: targetsQuery.data?.others || [],
    datavizType: targetsQuery.data?.dataviz_type ?? null,
    targetsLoading: targetsQuery.isLoading && !!artifactId && enabled,
  }
}
